export const SEO_SITE_URL = "https://eiraproducts.com";

const normalizeText = (value) => String(value || "").replace(/\s+/g, " ").trim();

export const limitSeoText = (value, maxLength = 160) => {
  const text = normalizeText(value);
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.slice(0, maxLength - 1).trimEnd()}…`;
};

export const absoluteSeoUrl = (pathOrUrl = "/") => {
  if (/^https?:\/\//i.test(pathOrUrl)) {
    const url = new URL(pathOrUrl);
    if (url.pathname.startsWith("/api/files/")) {
      return `${SEO_SITE_URL}${url.pathname}${url.search}`;
    }
    return pathOrUrl;
  }
  const path = pathOrUrl.startsWith("/") ? pathOrUrl : `/${pathOrUrl}`;
  return `${SEO_SITE_URL}${path}`;
};

export const productSeoPath = (product) => {
  const identifier = product.slug || product.id;
  return `/product/${encodeURIComponent(identifier)}`;
};

const productSearchIntent = (product) => {
  const content = [
    product.name,
    product.description,
    product.brand,
    ...(product.tags || []),
  ].join(" ");

  if (/\b(baby|infant|newborn|toddler|kids?)\b/i.test(content)) {
    return "safe natural baby care for Indian mothers";
  }
  if (/\b(skin|soap|face|body|hair|care|cream|lotion|oil|shampoo)\b/i.test(content)) {
    return "natural skincare and personal care in India";
  }
  return "natural everyday essentials in India";
};

const productPrice = (product) => {
  const prices = (product.variants || [])
    .map((variant) => Number(variant.price))
    .filter((price) => Number.isFinite(price) && price >= 0);
  const basePrice = Number(product.price);
  if (Number.isFinite(basePrice) && basePrice >= 0) {
    prices.push(basePrice);
  }
  return prices.length ? Math.min(...prices) : null;
};

const productInStock = (product) => {
  if (product.variants?.length) {
    return product.variants.some((variant) => Number(variant.stock) > 0);
  }
  return Number(product.stock) > 0;
};

export const buildProductSeo = (product, reviews = []) => {
  const productPath = productSeoPath(product);
  const canonical = absoluteSeoUrl(productPath);
  const intent = productSearchIntent(product);
  const suffix = `Shop ${intent} at Eira Products.`;
  const description = limitSeoText(
    product.description || `${product.name} from ${product.brand || "Eira Products"}.`,
    Math.max(40, 160 - suffix.length - 2),
  ).replace(/[.?!…]+$/, "");
  const metaDescription = `${description}. ${suffix}`;
  const imageUrls = (product.images || []).filter(Boolean).map(absoluteSeoUrl);
  const price = productPrice(product);
  const schema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    description: metaDescription,
    image: imageUrls,
    sku: product.sku || product.id,
    brand: {
      "@type": "Brand",
      name: product.brand || "Eira Products",
    },
  };

  if (price !== null) {
    schema.offers = {
      "@type": "Offer",
      url: canonical,
      priceCurrency: "INR",
      price: price.toFixed(2),
      availability: productInStock(product)
        ? "https://schema.org/InStock"
        : "https://schema.org/OutOfStock",
      itemCondition: "https://schema.org/NewCondition",
    };
  }

  if (reviews.length) {
    const ratingValue = reviews.reduce((total, review) => {
      return total + Number(review.rating || 0);
    }, 0) / reviews.length;
    if (Number.isFinite(ratingValue) && ratingValue > 0) {
      schema.aggregateRating = {
        "@type": "AggregateRating",
        ratingValue: ratingValue.toFixed(1),
        reviewCount: reviews.length,
      };
    }
  }

  return {
    title: `${product.name} | Eira Products`,
    description: metaDescription,
    canonical,
    image: imageUrls[0] || "",
    type: "product",
    schemaId: "eira-product-schema",
    schema,
  };
};